/**
 * # Pagination
 *
 * A component that provides pagination functionality.
 *
 * ## Props
 *
 * - `align` (string): The alignment of the pagination component. Possible values are 'left', 'center', or 'right'. (default: 'center')
 * - `defaultPage` (number): The default page to display. (default: 1)
 * - `className` (string): Additional CSS class name for the component.
 * - `variation` (string): The variation of the pagination component. Possible values are 'full', 'condensed', 'mini', or 'micro'. (default: 'condensed')
 * - `totalPages` (number): The total number of pages.
 * - `position` (string): The position of the pagination component. (default: 'bottom')
 * - `onChange` (function): The callback function triggered when a page is selected.
 *
 * ## Usage
 *
 * ```jsx
 * <Pagination
 *   align="center"
 *   defaultPage={1}
 *   className="pagination-component"
 *   variation="condensed"
 *   totalPages={10}
 *   position="bottom"
 *   onChange={(page) => {
 *     console.log(`Selected page: ${page}`);
 *   }}
 * />
 * ```
 */
import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {List as VirtualizedList} from 'react-virtualized';
import makeConstants from 'lib/makeConstants';

import IconButton from '../../molecules/IconButton/IconButton.react';
import Dropdown from '../../molecules/Dropdown/Dropdown.react';
import DropdownItem from '../../molecules/DropdownItem/DropdownItem.react';
import Button from '../../atoms/Button/Button.react';
import Text from '../../atoms/Text/Text.react';
import Icon from '../../atoms/Icon/Icon.react';
import './pagination.scss';

const alignOptions = makeConstants('left', 'center', 'right');
const variationOptions = makeConstants('full', 'condensed', 'mini', 'micro');
const variationLevel = {
  micro: 1,
  mini: 2,
  condensed: 3,
  full: 4
};
const pageElSize = 56;

export default class Pagination extends React.Component {
  static propTypes = {
    align: PropTypes.oneOf(Object.values(alignOptions)),
    defaultPage: PropTypes.number,
    className: PropTypes.string,
    variation: PropTypes.oneOf(Object.values(variationOptions)),
    totalPages: PropTypes.number,
    position: PropTypes.string,
    onChange: PropTypes.func
  };

  static defaultProps = {
    align: 'center',
    defaultPage: 1,
    onChange: () => {},
    variation: 'condensed',
    position: 'bottom'
  };

  constructor(props) {
    super(props);

    this.state = {
      currentPage: parseInt(props.defaultPage, 10)
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.totalPages !== this.props.totalPages) {
      this.selectPage(1);
    }
  }

  previousPage = () => {
    if (this.state.currentPage === 1) {
      return;
    }

    this.selectPage(this.state.currentPage - 1);
  };

  nextPage = () => {
    if (this.state.currentPage === this.props.totalPages) {
      return;
    }

    this.selectPage(this.state.currentPage + 1);
  };

  selectPage = (page) => {
    this.setState({currentPage: page});
    this.props.onChange(page);
  };

  rowRenderer = ({index, key, style}) => {
    return (
      <DropdownItem
        key={key}
        style={style}
        index={index}
        className='o-pagination__dropdown-page'
        selected={this.state.currentPage === index + 1}
        onClick={() => this.selectPage(index + 1)}
      >
        {index + 1}
      </DropdownItem>
    );
  };

  makeCurrentPageIndicator() {
    if (variationLevel[this.props.variation] < 2) {
      return null;
    }

    const maxHeight = pageElSize * 8;
    const pageElWidth = this.props.totalPages.toString().length * 15;
    const pageElHeight = this.props.totalPages * pageElSize;
    const height = pageElHeight > maxHeight ? maxHeight : pageElHeight;
    const width = pageElWidth > pageElSize ? pageElWidth : pageElSize;

    return (
      <Dropdown
        className='o-pagination__dropdown'
        withMinWidth={false}
        position={this.props.position}
        trigger={
          <Button
            aria-current
            className='o-pagination__page-button'
            color='secondary'
            variant='text'
            roundness='pill'
            size='l'
          >
            {this.state.currentPage} <Icon icon='caret-down' />
          </Button>
        }
      >
        <VirtualizedList
          height={height}
          width={width}
          rowHeight={pageElSize}
          rowRenderer={this.rowRenderer}
          rowCount={this.props.totalPages}
        />
      </Dropdown>
    );
  }

  render() {
    if (!this.props.totalPages || this.props.totalPages === 1) {
      return null;
    }

    const {align, className, defaultPage, onChange, position, totalPages, variation, ...rest} =
      this.props;

    return (
      <nav
        aria-label='Pagination'
        className={classnames('o-pagination', className, {
          [`o-pagination--${align}`]: align,
          [`o-pagination--${variation}`]: variation
        })}
        {...rest}
      >
        <IconButton
          onClick={this.previousPage}
          icon='chevron-left'
          label='previous'
          size='s'
          disabled={this.state.currentPage === 1}
        />
        {this.makeCurrentPageIndicator()}
        {variationLevel[variation] > 2 && (
          <Text className='o-pagination__divider' bold emphasis='lowest'>
            /
          </Text>
        )}
        {variationLevel[variation] > 2 && (
          <Button
            className='o-pagination__page-button'
            color='secondary'
            variant='text'
            roundness='pill'
            size='s'
            onClick={() => this.selectPage(totalPages)}
          >
            {totalPages}
          </Button>
        )}
        <IconButton
          onClick={this.nextPage}
          icon='chevron-right'
          label='next'
          size='s'
          disabled={this.state.currentPage === totalPages}
        />
      </nav>
    );
  }
}
